import type {
  CelestialBody,
  CelestialType,
  Civilization,
  Development,
  DevelopmentLevel,
  GameState,
  Personality,
  Rating1To5,
  Star,
  StarSystem,
  Suitability,
} from "../shared/game-types";
import { createBodyStockpile } from "./core";
import { DeterministicRandom, hash32 } from "./prng";

const SYSTEM_NAMES = ["曙光", "织女", "远帆", "静海", "赫利俄斯", "苍穹", "守望", "天苑"] as const;
const BODY_NAMES = ["阿斯特拉", "佩拉", "赫斯提亚", "特里同", "涅瑞伊德", "卡戎", "伊奥斯", "塞勒涅"] as const;
const CIVILIZATION_NAMES = ["伊利昂协作体", "涅墨西斯商团", "塔洛斯共同体", "静默观测议会", "远潮联盟"] as const;
const PERSONALITIES: readonly Personality[] = [
  "cooperative", "reciprocal", "commercial", "isolationist", "cautious", "expansionist", "militarist", "technologist",
  "conservative", "opportunist", "hegemonic", "defensive", "longTerm", "suspicious", "resourceHungry",
];

function stableId(prefix: string, seed: string, key: string): string {
  return `${prefix}_${hash32(`${seed}:${key}`).toString(36).padStart(7, "0")}`;
}

function starFor(rng: DeterministicRandom, name: string): Star {
  const massSolar = rng.between(0.58, 1.35);
  const luminositySolar = Math.max(0.08, massSolar ** 3.7);
  return {
    name,
    massSolar: round(massSolar, 3),
    luminositySolar: round(luminositySolar, 3),
    temperatureK: Math.round(5772 * massSolar ** 0.48),
    ageGyr: round(rng.between(1.2, 10.5), 2),
    metallicity: round(rng.between(0.45, 1.7), 2),
    activity: clampRating(Math.round(rng.between(1, 5))),
  };
}

function round(value: number, places = 2): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

function clampRating(value: number): Rating1To5 {
  return Math.max(1, Math.min(5, value)) as Rating1To5;
}

function bodyType(rng: DeterministicRandom, orbitAu: number, metallicity: number): CelestialType {
  const roll = rng.float();
  if (orbitAu < 0.18) return roll < 0.65 ? "lava" : "rocky";
  if (orbitAu < 1.9) return roll < 0.2 * metallicity ? "metallic" : "rocky";
  if (orbitAu < 5.5) return roll < 0.48 ? "gasGiant" : roll < 0.7 ? "iceMoon" : "rocky";
  return roll < 0.36 ? "iceGiant" : roll < 0.78 ? "iceMoon" : "comet";
}

function createBody(
  seed: string,
  systemId: string,
  index: number,
  star: Star,
  rng: DeterministicRandom,
  forced?: Partial<Pick<CelestialBody, "name" | "type" | "orbitAu" | "development" | "suitability" | "environment" | "quality">>,
): CelestialBody {
  const orbitAu = forced?.orbitAu ?? round(0.22 * 1.72 ** index * rng.between(0.88, 1.12), 3);
  const type = forced?.type ?? bodyType(rng, orbitAu, star.metallicity);
  const radiusByType: Record<CelestialType, [number, number]> = {
    rocky: [0.45, 1.65], metallic: [0.08, 0.7], iceMoon: [0.12, 0.65], gasGiant: [7, 13], iceGiant: [3, 6],
    lava: [0.45, 1.45], comet: [0.01, 0.08], rogue: [0.4, 2.5],
  };
  const radiusEarth = rng.between(...radiusByType[type]);
  const densityBase: Record<CelestialType, number> = {
    rocky: 5, metallic: 7, iceMoon: 2.1, gasGiant: 1.2, iceGiant: 1.7, lava: 5.8, comet: 0.7, rogue: 3.4,
  };
  const density = densityBase[type] * rng.between(0.84, 1.16);
  const massEarth = type === "gasGiant" || type === "iceGiant"
    ? radiusEarth ** 1.8 * density / 3.5
    : radiusEarth ** 3 * density / 5.51;
  const gravityG = massEarth / radiusEarth ** 2;
  const equilibrium = 278 * star.luminositySolar ** 0.25 / Math.sqrt(Math.max(0.03, orbitAu));
  const greenhouse = type === "gasGiant" || type === "iceGiant" ? 25 : type === "rocky" ? rng.between(-12, 45) : 0;
  const meanTemperature = equilibrium + greenhouse;
  const pressureBar = type === "gasGiant" || type === "iceGiant" ? rng.between(40, 600) : type === "rocky" ? rng.between(0, 4) : 0;
  const radiation = clampRating(Math.round(star.activity * Math.max(0.35, 1 / Math.sqrt(orbitAu))));
  const magneticField = clampRating(Math.round(rng.between(1, type === "gasGiant" ? 5 : 4)));
  const environmentalPenalty =
    (meanTemperature < 180 || meanTemperature > 390 ? 2 : meanTemperature < 235 || meanTemperature > 330 ? 1 : 0)
    + (gravityG > 2.2 ? 1 : 0) + (radiation >= 4 ? 1 : 0) + (pressureBar > 15 ? 1 : 0);
  const environment = forced?.environment ?? clampRating(1 + environmentalPenalty);
  const quality = forced?.quality ?? clampRating(Math.round(1 + star.metallicity * 1.4 + density / 4 + rng.between(-1, 1)));
  let suitability: Suitability = 0;
  if (!["gasGiant", "iceGiant", "lava", "comet"].includes(type)) {
    const score = 5 - environment + (meanTemperature >= 240 && meanTemperature <= 325 ? 1 : 0) + (gravityG >= 0.5 && gravityG <= 1.5 ? 1 : 0);
    suitability = Math.max(0, Math.min(5, Math.round(score))) as Suitability;
  }
  return {
    id: stableId("body", seed, `${systemId}:${index}`),
    systemId,
    name: forced?.name ?? `${rng.pick(BODY_NAMES)} ${index + 1}`,
    type,
    orbitAu,
    eccentricity: round(rng.between(0, type === "comet" ? 0.75 : 0.18), 4),
    inclinationDeg: round(rng.between(0, type === "comet" ? 35 : 8), 2),
    orbitalPeriodYears: round(Math.sqrt(orbitAu ** 3 / star.massSolar), 3),
    rotationHours: round(rng.between(7, 260), 1),
    radiusEarth: round(radiusEarth, 3),
    massEarth: round(massEarth, 3),
    density: round(density, 2),
    gravityG: round(gravityG, 3),
    escapeVelocityKms: round(11.186 * Math.sqrt(Math.max(0.001, massEarth / radiusEarth)), 2),
    temperatureK: [Math.round(meanTemperature - rng.between(8, 55)), Math.round(meanTemperature + rng.between(8, 55))],
    atmosphere: type === "gasGiant" ? "H₂/He" : type === "iceGiant" ? "H₂/He/CH₄" : pressureBar > 0.5 ? "N₂/CO₂/微量气体" : pressureBar > 0.02 ? "稀薄 CO₂/N₂" : "近真空",
    pressureBar: round(pressureBar, 3),
    radiation,
    magneticField,
    environment,
    quality,
    suitability: forced?.suitability ?? suitability,
    development: forced?.development ?? 0,
    baseYield: round(8 + quality ** 2 * rng.between(3.5, 7.5), 2),
    stableYield: round(3 + quality * rng.between(1.5, 3.5), 2),
    depletionRate: round(rng.between(0.008, 0.045) / quality, 5),
  };
}

export interface GeneratedWorld {
  systems: StarSystem[];
  developments: Record<string, Development>;
  civilizations: Civilization[];
}

export function generateWorld(worldSeed: string): GeneratedWorld {
  const seed = worldSeed.trim() || "default-world";
  const rng = new DeterministicRandom(`world:v1:${seed}`);
  const systems: StarSystem[] = [];
  const developments: Record<string, Development> = {};
  const systemCount = 5;

  for (let systemIndex = 0; systemIndex < systemCount; systemIndex += 1) {
    const systemId = stableId("system", seed, String(systemIndex));
    const name = systemIndex === 0 ? "母星系" : `${rng.pick(SYSTEM_NAMES)}-${systemIndex}`;
    const star = starFor(rng, name);
    const bodyCount = systemIndex === 0 ? 6 : rng.integer(3, 8);
    const bodies: CelestialBody[] = [];
    for (let bodyIndex = 0; bodyIndex < bodyCount; bodyIndex += 1) {
      const forced = systemIndex === 0 && bodyIndex === 2
        ? { name: "家园", type: "rocky" as const, orbitAu: 1, development: 6 as DevelopmentLevel, suitability: 5 as Suitability, environment: 1 as Rating1To5, quality: 3 as Rating1To5 }
        : systemIndex === 0 && bodyIndex === 3
          ? { name: "赤原", type: "rocky" as const, orbitAu: 1.58, development: 1 as DevelopmentLevel, suitability: 2 as Suitability, environment: 3 as Rating1To5, quality: 3 as Rating1To5 }
          : systemIndex === 0 && bodyIndex === 4
            ? { name: "金属带", type: "metallic" as const, orbitAu: 2.7, development: 2 as DevelopmentLevel, suitability: 0 as Suitability, environment: 2 as Rating1To5, quality: 4 as Rating1To5 }
            : undefined;
      const body = createBody(seed, systemId, bodyIndex, star, rng, forced);
      bodies.push(body);
      if (body.development > 0) {
        developments[body.id] = {
          bodyId: body.id,
          level: body.development,
          stockpile: createBodyStockpile(body.development),
          installations: body.development === 6
            ? { probeStation: 1, miningOutpost: 2, orbitalProcessor: 1, researchBase: 1, industrialBase: 1, colony: 1 }
            : {},
          constructionQueue: [],
          condition: 100,
          exploitationYears: body.development === 6 ? 18 : 0,
        };
      }
    }
    systems.push({
      id: systemId,
      name,
      distanceLy: systemIndex === 0 ? 0 : round(rng.between(3.6, 19) + systemIndex * 1.8, 2),
      discovered: systemIndex === 0 || systemIndex === 1,
      observed: systemIndex === 0,
      star,
      bodies,
    });
  }

  const civilizationLevels = [3, 5, 7] as const;
  const civilizations: Civilization[] = systems.slice(1, 4).map((system, index) => ({
    id: stableId("civ", seed, String(index)),
    name: rng.pick(CIVILIZATION_NAMES),
    level: civilizationLevels[index] ?? 3,
    personality: [rng.pick(PERSONALITIES), rng.pick(PERSONALITIES)],
    advantage: rng.pick(["energy", "mining", "materials", "research", "shipping", "industry"] as const),
    known: index === 0,
    contacted: false,
    militaryEstimate: "unknown",
    homeSystemId: system.id,
  }));

  return { systems, developments, civilizations };
}

export function findBody(state: Pick<GameState, "systems">, bodyId: string): CelestialBody | undefined {
  for (const system of state.systems) {
    const body = system.bodies.find((candidate) => candidate.id === bodyId);
    if (body) return body;
  }
  return undefined;
}

export function findSystemForBody(state: Pick<GameState, "systems">, bodyId: string): StarSystem | undefined {
  return state.systems.find((system) => system.bodies.some((body) => body.id === bodyId));
}
